<template>
  <div class="relative min-h-screen w-full bg-gradient-to-br from-gray-100 to-blue-50">

    <!-- Sliding Sidebar Filter -->
    <transition name="slide">
      <aside v-show="showFilter" class="cp-sidebar fixed top-0 left-0 h-full w-72 bg-white z-40 flex flex-col border-r border-gray-200">
        <div class="px-6 pt-24 pb-4 border-b border-gray-100">
          <h2 class="text-base font-bold text-gray-800 flex items-center gap-2 m-0">
            <font-awesome-icon :icon="['fas', 'sliders-h']" class="text-blue-600" />
            Filters
          </h2>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Entity</label>
            <select v-model="filterByEntity" class="cp-select w-full">
              <option value="">All</option>
              <option :value="item.entity" v-for="(item, index) in filteredEntities" :key="index">
                {{ getEntityName(item.entity) }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Industry</label>
            <select v-model="filterByIndustry" class="cp-select w-full">
              <option :value="null">All</option>
              <option :value="item.id" v-for="(item, index) in filteredIndustries" :key="index">{{ item.name }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Type of collaboration</label>
            <checkbox-picker
              v-model="filterByTypeOfCollaboration"
              :options="typeOfCollaborationOptions"
              search-placeholder="Search by name"
              empty-search-text="No types match your search."
              item-name="type"
              :compact="true"
            ></checkbox-picker>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Classifications</label>
            <checkbox-picker
              v-model="filterByClassifications"
              :options="classificationFilterOptions"
              search-placeholder="Search by name"
              empty-search-text="No classifications match your search."
              item-name="classification"
              :compact="true"
            ></checkbox-picker>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tier</label>
            <checkbox-picker
              v-model="filterByTier"
              :options="tierFilterOptions"
              search-placeholder="Search by name"
              empty-search-text="No tiers match your search."
              item-name="tier"
              :compact="true"
            ></checkbox-picker>
          </div>
        </div>
        <div class="px-6 py-4 flex gap-3 border-t border-gray-100">
          <button @click.stop="clearFilters"
            class="cp-btn-outline flex-1">
            Clear
          </button>
          <button @click.stop="applyFilters"
            class="cp-btn-primary flex-1">
            Apply
          </button>
        </div>
      </aside>
    </transition>
    <div v-if="showFilter" class="fixed inset-0 bg-black bg-opacity-20 z-30" @click="showFilter = false"></div>

    <div class="container mx-auto px-4 sm:px-8 py-8">

      <!-- Page Header -->
      <nav class="text-sm text-gray-500 mb-5 flex items-center">
        <router-link to="/dashboard" class="hover:text-blue-700 font-medium">Dashboard</router-link>
        <span class="mx-1.5 text-gray-300">/</span>
        <router-link to="/partnerships" class="hover:text-blue-700 font-medium">Partnerships</router-link>
        <span class="mx-1.5 text-gray-300">/</span>
        <span class="text-gray-800 font-semibold">Current Partners</span>
      </nav>

      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p class="text-xs font-bold uppercase tracking-widest text-blue-600 m-0 mb-1">Partnerships</p>
          <h1 class="text-3xl font-extrabold text-gray-900 m-0">Current Partners</h1>
          <p class="text-gray-500 mt-1 mb-0 text-sm">Manage active partnerships, track tiers, and monitor collaboration.</p>
        </div>
        <div class="flex items-center gap-3">
          <button @click="toggleFilter"
            class="cp-btn-ghost" title="Filters">
            <font-awesome-icon :icon="['fas', 'filter']" />
            <span>Filters</span>
          </button>
          <button v-if="hasPermission('current-partners-export-excel')" @click="generateReport"
            class="cp-btn-ghost" title="Export Excel">
            <font-awesome-icon :icon="['fas', 'file-excel']" />
            <span>Export</span>
          </button>
          <button v-if="hasPermission('current-partners-create')" @click.stop="createModal = true"
            class="cp-btn-primary">
            <icon-plus class="w-4 h-4"></icon-plus>
            <span>Add Partner</span>
          </button>
        </div>
      </div>

      <!-- Tier Cards -->
      <div class="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div class="cp-stat-card cp-stat-card--total">
          <div class="cp-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'handshake']" />
          </div>
          <div class="cp-stat-card__body">
            <p class="cp-stat-card__value">{{ formatNumber(partnersStatistics.totalPartners) }}</p>
            <p class="cp-stat-card__label">Total Partners</p>
          </div>
        </div>
        <div class="cp-stat-card cp-stat-card--strategic cp-stat-card--clickable" @click="onTierChartClick('Strategic')">
          <div class="cp-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'crown']" />
          </div>
          <div class="cp-stat-card__body">
            <p class="cp-stat-card__value">{{ getTierCount('Strategic') }}</p>
            <p class="cp-stat-card__label">Strategic</p>
            <p class="cp-stat-card__hint">3+ focus areas</p>
          </div>
        </div>
        <div class="cp-stat-card cp-stat-card--active cp-stat-card--clickable" @click="onTierChartClick('Active')">
          <div class="cp-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'bolt']" />
          </div>
          <div class="cp-stat-card__body">
            <p class="cp-stat-card__value">{{ getTierCount('Active') }}</p>
            <p class="cp-stat-card__label">Active</p>
            <p class="cp-stat-card__hint">2 focus areas</p>
          </div>
        </div>
        <div class="cp-stat-card cp-stat-card--engaged cp-stat-card--clickable" @click="onTierChartClick('Engaged')">
          <div class="cp-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'hand-holding-heart']" />
          </div>
          <div class="cp-stat-card__body">
            <p class="cp-stat-card__value">{{ getTierCount('Engaged') }}</p>
            <p class="cp-stat-card__label">Engaged</p>
            <p class="cp-stat-card__hint">1 focus area</p>
          </div>
        </div>
        <div class="cp-stat-card cp-stat-card--shelved cp-stat-card--clickable" @click="onTierChartClick('Shelved')">
          <div class="cp-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'archive']" />
          </div>
          <div class="cp-stat-card__body">
            <p class="cp-stat-card__value">{{ getTierCount('Shelved') }}</p>
            <p class="cp-stat-card__label">Shelved</p>
            <p class="cp-stat-card__hint">To be upgraded or discontinued</p>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
        <PartnersByEntityChart :data="partnersStatistics.partnersByEntity" @entity-clicked="onEntityChartClick" />
        <PartnersByTypeOfCollaborationChart :data="partnersStatistics.partnersByTypeOfCollaboration" @type-of-collaboration-clicked="onTypeOfCollaborationChartClick" />
        <PartnersByTierChart :data="partnersStatistics.partnersByTier" @tier-clicked="onTierChartClick" />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <PartnersByIndustryChart
          :key="'industry-' + statsChartKey"
          :data="partnersStatistics.partnersByIndustry"
          @industry-clicked="onIndustryChartClick"
        />
        <PartnersByClassificationChart
          :key="'classification-' + statsChartKey"
          :data="partnersStatistics.partnersByClassification"
          :fill-to-count="industryChartCount"
          @classification-clicked="onClassificationChartClick"
        />
      </div>

      <!-- Partners Table -->
      <section class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="flex items-center justify-between flex-wrap gap-4 px-6 md:px-8 pt-6 pb-4">
          <h2 class="text-lg font-bold text-gray-900 m-0">Partners List</h2>
          <div class="w-full sm:w-auto">
            <search ref="searchRef" @on-search="onSearch" class-list="px-4 h-9"></search>
          </div>
        </div>

        <div v-if="isLoading" class="px-6 md:px-8 pb-6">
          <table-shimmer :titleHeaders="[
            '#', 'Name', 'Logo', 'Entity', 'Industry', 'Type of collaboration',
            'Classifications', 'Tier', 'Landing Page Visibility', 'Last Updated', 'Actions',
          ]" :rows="first"></table-shimmer>
        </div>

        <div v-else-if="!hasItems" class="py-20 text-center">
          <p class="text-lg font-bold text-gray-800 mb-1">No partners found</p>
          <p class="text-gray-500 text-sm mb-0">Try adjusting your filters or search term.</p>
        </div>

        <div v-else>
          <div class="overflow-x-auto">
            <table class="min-w-full text-left text-sm">
              <thead>
                <tr class="cp-thead-row">
                  <th class="cp-th w-12">#</th>
                  <th class="cp-th">Name</th>
                  <th class="cp-th">Logo</th>
                  <th class="cp-th">Entity</th>
                  <th class="cp-th">Industry</th>
                  <th class="cp-th">Type of collaboration</th>
                  <th class="cp-th">Classifications</th>
                  <th class="cp-th">Tier</th>
                  <th class="cp-th whitespace-nowrap">Landing Page</th>
                  <th class="cp-th whitespace-nowrap">Last Updated</th>
                  <th class="cp-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in items" :key="index"
                  :class="['cp-row', hasChildPartners(item) ? 'cp-row--subsidiary' : '']">
                  <td class="cp-td font-medium whitespace-nowrap">{{ paginatorInfo.firstItem + index }}</td>
                  <td class="cp-td">
                    <span class="flex items-center gap-2">
                      <button
                        type="button"
                        class="cp-name-link"
                        :title="'Open ' + item.name"
                        @click.stop="openPartnerPage(item)"
                      >
                        {{ item.name }}
                      </button>
                      <span v-if="hasChildPartners(item)"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800"
                        title="Has child partners">
                        <font-awesome-icon :icon="['fas', 'sitemap']" class="mr-1" />
                        {{ getChildPartnersCount(item) }}
                      </span>
                    </span>
                  </td>
                  <td class="cp-td">
                    <img class="h-10 w-10 rounded-lg object-contain bg-gray-50 border border-gray-100" :src="getSrc(item.logo)" alt="" />
                  </td>
                  <td class="cp-td">{{ getEntityName(item.entity) }}</td>
                  <td class="cp-td text-gray-600">{{ getIndustryName(item) }}</td>
                  <td class="cp-td text-gray-600">{{ item.type_of_collaboration || '-' }}</td>
                  <td class="cp-td text-gray-600">{{ formatClassifications(item) }}</td>
                  <td class="cp-td">
                    <span v-if="getPartnerTier(item)" :class="partnerTierBadgeClass(getPartnerTier(item))">
                      {{ getPartnerTier(item) }}
                    </span>
                    <span v-else class="text-gray-300">-</span>
                  </td>
                  <td class="cp-td">
                    <div class="landing-visibility-cell" @click.stop>
                      <label v-if="hasPermission('current-partners-edit')" class="landing-visibility-switch"
                        :class="{ 'landing-visibility-switch-disabled': landingVisibilityUpdatingToken === item.token }">
                        <input type="checkbox" class="landing-visibility-switch-input"
                          :checked="isShowOnLandingPage(item)"
                          :disabled="landingVisibilityUpdatingToken === item.token"
                          @change="onLandingVisibilityToggle(item, $event)" />
                        <span class="landing-visibility-switch-slider"></span>
                      </label>
                      <span v-else
                        :class="['landing-visibility-badge', isShowOnLandingPage(item) ? 'landing-visibility-badge-yes' : 'landing-visibility-badge-no']">
                        {{ isShowOnLandingPage(item) ? 'Visible' : 'Hidden' }}
                      </span>
                    </div>
                  </td>
                  <td class="cp-td text-gray-500 whitespace-nowrap">{{ getFormattedDate(item.updated_at) }}</td>
                  <td class="cp-td">
                    <div class="flex gap-1">
                      <button v-if="hasPermission('current-partners-index')" @click.stop="openPartnerPage(item)"
                        class="cp-action-btn" title="Open page">
                        <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                      </button>
                      <button v-if="hasPermission('current-partners-index')" @click.stop="viewItem(item)"
                        class="cp-action-btn" title="Quick view">
                        <icon-eye class="w-4 h-4"></icon-eye>
                      </button>
                      <button v-if="hasPermission('view-mou-effectiveness-card')"
                        @click.stop="viewMouEffectivenessCard(item.id, item.name)"
                        class="cp-action-btn" title="MoU Effectiveness Card">
                        <font-awesome-icon :icon="['fas', 'clipboard-check']" />
                      </button>
                      <button v-if="hasPermission('current-partners-edit')" @click.stop="editItem(item)"
                        class="cp-action-btn" title="Edit">
                        <icon-pen class="w-4 h-4"></icon-pen>
                      </button>
                      <button v-if="hasPermission('current-partners-delete')" @click.stop="deleteItem(item.token)"
                        class="cp-action-btn cp-action-btn--danger" title="Delete">
                        <icon-trash class="w-4 h-4"></icon-trash>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="px-6 md:px-8 py-4 border-t border-gray-100">
            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
          </div>
        </div>
      </section>
    </div>

    <!-- Modals -->
    <transition name="fade" mode="out-in">
      <base-full-modal v-if="createModal" :title="`Create Current Partner`" @close="closeModal">
        <create class="text-left" :entities="filteredEntities" @created="onCreated" @close="closeModal"></create>
      </base-full-modal>
    </transition>

    <transition name="fade" mode="out-in">
      <base-full-modal v-if="editModal" :title="`Edit Current Partner - ${selectedItem.name}`" @close="closeModal">
        <edit class="text-left" :entities="filteredEntities" :selected-item="selectedItem" @updated="onUpdated" @close="closeModal"></edit>
      </base-full-modal>
    </transition>

    <transition name="fade" mode="out-in">
      <base-full-modal v-if="isViewItem" :title="`View Current Partner - ${selectedItem.name}`" @close="closeModal">
        <current-partners-view class="text-left" :item="selectedItem" @close="closeModal"></current-partners-view>
      </base-full-modal>
    </transition>

    <transition name="fade" mode="out-in">
      <base-full-modal v-if="isViewMouEffectivenessCard" :title="`MoU Effectiveness Card - ${selectedTitle}`" @close="closeMouEffectivenessCard">
        <mou-effectiveness-card :current-partner-id="selectedId" from="current-partners-index" @close="closeMouEffectivenessCard"></mou-effectiveness-card>
      </base-full-modal>
    </transition>

    <tier-drilldown-modal
      :visible="isChartDrilldownOpen"
      :dimension="chartDrilldownDimension"
      :dimension-value="chartDrilldownValue"
      :dimension-label="chartDrilldownLabel"
      :base-filters="tierDrilldownBaseFilters"
      :show-export-excel="hasPermission('current-partners-export-excel')"
      @close="closeChartDrilldownModal"
    ></tier-drilldown-modal>
  </div>
</template>

<script>
import IconPlus from "../../components/icons/IconPlus.vue";
import IconEye from "../../components/icons/IconEye.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from "../../components/shared/TableShimmer.vue";
import { formattedDate } from "../../helpers/utils";
import Create from "./create.vue";
import Edit from "./edit.vue";
import CurrentPartnersView from "./view.vue";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import Search from "../../components/shared/search.vue";
import MouEffectivenessCard from "./MouEffectivenessCard/index.vue";
import PartnersByEntityChart from "./charts/PartnersByEntityChart.vue";
import PartnersByIndustryChart from "./charts/PartnersByIndustryChart.vue";
import PartnersByTypeOfCollaborationChart from "./charts/PartnersByTypeOfCollaborationChart.vue";
import PartnersByClassificationChart from "./charts/PartnersByClassificationChart.vue";
import PartnersByTierChart from "./charts/PartnersByTierChart.vue";
import TierDrilldownModal from "./TierDrilldownModal.vue";
import CheckboxPicker from "../../components/shared/CheckboxPicker.vue";
import {
  evaluatePartnerTier,
  getTierBadgeClass,
} from "../../helpers/partnerTier";

var EXPORT_PARTNERS_QUERY = gql`
  query ExportCurrentPartners(
    $page: Int
    $limit: Int
    $is_pipeline: Boolean
    $entity: String
    $industry: Int
    $type_of_collaboration: [String]
    $classifications: [String]
    $tier: [String]
    $search: String
  ) {
    exportCurrentPartners(
      page: $page
      limit: $limit
      is_pipeline: $is_pipeline
      entity: $entity
      industry: $industry
      type_of_collaboration: $type_of_collaboration
      classifications: $classifications
      tier: $tier
      search: $search
    ) {
      error
      error_code
      message
      data {
        name
        entity
        industry
        industry_relation { id name }
        type_of_collaboration
        classifications
        updated_at
        contact_person {
          name
          designation
          phone
          email
        }
      }
    }
  }
`;

function runDetachedPartnerExport(ctx) {
  var store = ctx.store;
  var apolloClient = ctx.apolloClient;
  var jobId = ctx.jobId;
  var fileName = ctx.fileName;
  var variables = ctx.variables;
  var columns = ctx.columns;
  var mapRow = ctx.mapRow;

  var allRows = [];
  var page = 1;
  var limit = 200;

  function fetchNext() {
    var vars = {};
    var keys = Object.keys(variables);
    for (var i = 0; i < keys.length; i++) {
      vars[keys[i]] = variables[keys[i]];
    }
    vars.page = page;
    vars.limit = limit;

    apolloClient
      .query({
        query: EXPORT_PARTNERS_QUERY,
        fetchPolicy: "network-only",
        variables: vars,
      })
      .then(function (result) {
        var responseData = result && result.data
          ? result.data.exportCurrentPartners
          : null;

        if (!responseData || responseData.error) {
          throw new Error(
            (responseData && responseData.message) || "Server error"
          );
        }

        if (responseData.data && responseData.data.length > 0) {
          for (var i = 0; i < responseData.data.length; i++) {
            allRows.push(mapRow(responseData.data[i]));
          }

          var percent = Math.min(95, Math.round(
            (allRows.length / Math.max(allRows.length + limit, 1)) * 100
          ));

          store.dispatch("exportManager/updateJob", {
            id: jobId,
            progress: percent,
            currentSheet: "Current Partners (" + allRows.length + " rows)",
          });

          page++;
          fetchNext();
        } else {
          finishExport();
        }
      })
      .catch(function (error) {
        if (allRows.length > 0) {
          finishExport();
        } else {
          store.dispatch("exportManager/failJob", {
            id: jobId,
            error: (error && error.message) || "Export failed",
          });
        }
      });
  }

  function finishExport() {
    try {
      if (allRows.length === 0) {
        store.dispatch("exportManager/failJob", {
          id: jobId,
          error: "No data available for export",
        });
        return;
      }

      var colKeys = columns.map(function (col) { return col.key; });

      var rows = allRows.map(function (row) {
        var out = {};
        for (var i = 0; i < columns.length; i++) {
          var key = columns[i].key;
          out[key] = row[key] !== undefined && row[key] !== null ? row[key] : "";
        }
        return out;
      });

      var worksheet = xlsx.utils.json_to_sheet(rows, { header: colKeys });

      var columnWidths = [];
      for (var i = 0; i < columns.length; i++) {
        var column = String.fromCharCode(65 + (i % 26));
        var r = Math.floor(i / 26);
        var columnLetter = r > 0 ? String.fromCharCode(64 + r) + column : column;
        worksheet[columnLetter + "1"] = { v: columns[i].header, t: "s" };
        columnWidths.push({ width: Math.max(40, columns[i].header.length + 5) });
      }

      worksheet["!cols"] = columnWidths;

      var workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      xlsx.writeFile(workbook, fileName);

      store.dispatch("exportManager/completeJob", {
        id: jobId,
        rowCount: allRows.length,
      });
    } catch (error) {
      store.dispatch("exportManager/failJob", {
        id: jobId,
        error: (error && error.message) || "Export failed",
      });
    }
  }

  fetchNext();
}

const UPDATE_LANDING_VISIBILITY_MUTATION = gql`
  mutation UpdateCurrentPartnerLandingVisibility($token: String!, $show_on_landing_page: Boolean!) {
    updateCurrentPartnerLandingVisibility(token: $token, show_on_landing_page: $show_on_landing_page) {
      error
      message
    }
  }
`;

export default {
  name: "current-partners-index",
  components: {
    IconPlus,
    IconEye,
    IconPen,
    IconTrash,
    Pagination,
    TableShimmer,
    Create,
    Edit,
    CurrentPartnersView,
    Search,
    MouEffectivenessCard,
    PartnersByEntityChart,
    PartnersByIndustryChart,
    PartnersByTypeOfCollaborationChart,
    PartnersByClassificationChart,
    PartnersByTierChart,
    TierDrilldownModal: TierDrilldownModal,
    CheckboxPicker: CheckboxPicker,
  },
  data() {
    return {
      entities: [],
      industries: [],
      current_partners: {
        data: [],
        paginatorInfo: {},
      },
      first: 10,
      page: 1,
      createModal: false,
      editModal: false,
      isViewItem: false,
      selectedItem: null,
      isLoading: false,
      filterByEntity: "",
      filterByIndustry: null,
      filterByTypeOfCollaboration: null,
      typeOfCollaborationOptions: ["NDA", "MOU", "Agreement", "Contract"],
      filterByClassifications: null,
      classificationFilterOptions: [
        "Employment-focused",
        "Training-focused",
        "Research-focused",
        "Sponsorship-focused",
        "Project-focused",
        "General partnership",
        "Shelved (to be upgraded or discontinued)",
      ],
      filterByTier: null,
      tierFilterOptions: ["Strategic", "Active", "Engaged", "Shelved"],
      searchText: "",
      selectedId: null,
      selectedTitle: "",
      isViewMouEffectivenessCard: false,
      showFilter: false,
      landingVisibilityUpdatingToken: null,
      isChartDrilldownOpen: false,
      chartDrilldownDimension: "",
      chartDrilldownValue: null,
      chartDrilldownLabel: "",
      partnersStatistics: {
        totalPartners: 0,
        partnersByEntity: [],
        partnersByIndustry: [],
        partnersByTypeOfCollaboration: [],
        partnersByClassification: [],
        partnersByTier: [],
      },
    };
  },
  created() {
    this.fetchCurrentPartners();
    this.fetchPartnersStatistics();
  },
  watch: {
    current_partners(newVal) {
      if (newVal.error) {
        if (newVal.error_code == 403) {
          this.$router.replace("/no-permission");
        } else if (newVal.error_code == 401) {
          this.$router.replace("/");
        } else {
          alert(newVal.message);
          this.$router.replace("/");
        }
      }
    },
    filterByEntity() {
      this.filterByIndustry = null;
    },
    searchText() {
      this.page = 1;
      this.fetchCurrentPartners();
      this.fetchPartnersStatistics();
    },
  },
  computed: {
    items() {
      return this.current_partners.data;
    },
    hasItems() {
      if (this.items && this.items.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    paginatorInfo() {
      return this.current_partners.paginatorInfo;
    },
    role() {
      return this.$store.getters.role;
    },
    filteredEntities() {
      // Add IAT entity to the list for this page only
      const entities = this.entities.data || [];
      const iatEntity = {
        id: "IAT",
        entity: "IAT",
        __typename: "Entity"
      };

      // Check if IAT already exists to avoid duplicates
      const hasIAT = entities.some(entity => entity.entity === "IAT");
      if (!hasIAT) {
        return [...entities, iatEntity];
      }

      return entities;
    },
    filteredIndustries() {
      return this.industries;
    },
    industryChartCount() {
      var list = this.partnersStatistics && this.partnersStatistics.partnersByIndustry
        ? this.partnersStatistics.partnersByIndustry
        : [];
      return list.length;
    },
    statsChartKey() {
      var typeFilter = this.filterByTypeOfCollaboration && this.filterByTypeOfCollaboration.length > 0
        ? this.filterByTypeOfCollaboration.join(",")
        : "";
      var classFilter = this.filterByClassifications && this.filterByClassifications.length > 0
        ? this.filterByClassifications.join(",")
        : "";
      var tierFilter = this.filterByTier && this.filterByTier.length > 0
        ? this.filterByTier.join(",")
        : "";
      var industryFilter = this.filterByIndustry != null ? String(this.filterByIndustry) : "";
      return [
        this.filterByEntity || "",
        industryFilter,
        typeFilter,
        classFilter,
        tierFilter,
        this.searchText || "",
        String(this.partnersStatistics.totalPartners || 0),
      ].join("|");
    },
    tierDrilldownBaseFilters() {
      return {
        is_pipeline: false,
        entity: this.filterByEntity || null,
        industry: this.filterByIndustry ? parseInt(this.filterByIndustry, 10) : null,
        type_of_collaboration: this.filterByTypeOfCollaboration && this.filterByTypeOfCollaboration.length > 0
          ? this.filterByTypeOfCollaboration
          : null,
        classifications: this.filterByClassifications && this.filterByClassifications.length > 0
          ? this.filterByClassifications
          : null,
        tier: this.filterByTier && this.filterByTier.length > 0
          ? this.filterByTier
          : null,
        search: this.searchText || null,
      };
    },
  },
  apollo: {
    entities() {
      return {
        query: gql`
          query GetEntityList {
            entities {
              data {
                id
                entity
              }
            }
          }
        `,
        fetchPolicy: "cache-and-network",
      };
    },
    industries() {
      return {
        query: gql`
          query GetCurrentPartnerIndustries(
            $entity: String
            $is_pipeline: Boolean
          ) {
            industries(entity: $entity, is_pipeline: $is_pipeline) {
              id
              name
            }
          }
        `,
        variables() {
          return {
            entity: this.filterByEntity,
            is_pipeline: false,
          };
        },
        fetchPolicy: "cache-and-network",
      };
    },
  },
  methods: {
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    clearFilters() {
      this.toggleFilter();
      this.filterByEntity = "";
      this.filterByIndustry = null;
      this.filterByTypeOfCollaboration = null;
      this.filterByClassifications = null;
      this.filterByTier = null;
      this.$refs.searchRef.clearInput();
      this.page = 1;
      this.fetchCurrentPartners();
      this.fetchPartnersStatistics();
    },
    applyFilters() {
      this.toggleFilter();
      this.page = 1;
      this.fetchCurrentPartners();
      this.fetchPartnersStatistics();
    },
    async fetchCurrentPartners() {
      this.isLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetAllCurrentPartners(
              $first: Int!
              $page: Int!
              $is_pipeline: Boolean
              $entity: String
              $industry: Int
              $type_of_collaboration: [String]
              $classifications: [String]
              $tier: [String]
              $search: String
            ) {
              current_partners(
                first: $first
                page: $page
                is_pipeline: $is_pipeline
                entity: $entity
                industry: $industry
                type_of_collaboration: $type_of_collaboration
                classifications: $classifications
                tier: $tier
                search: $search
              ) {
                error
                error_code
                message
                data {
                  id
                  token
                  name
                  logo
                  show_on_landing_page
                  entity
                  location
                  industry
                  industry_relation { id name }
                  type_of_collaboration
                  classifications
                  parent_partner_id
                  parent_partner {
                    id
                    name
                    token
                    is_pipeline
                  }
                  subsidiary_partners {
                    id
                    token
                    name
                    logo
                    entity
                    industry
                    industry_relation { id name }
                    type_of_collaboration
                    classifications
                    is_pipeline
                  }
                  updated_at
                  is_signed
                  approved_at
                  is_bot_approval
                  is_md_approval
                  is_line_manager_approval
                  contact_person {
                    name
                    phone
                    email
                    designation
                  }
                  joint_committee_members {
                    id
                    user_id
                    name
                    title
                    entity
                    display_entity
                    mobile
                    email
                    is_internal
                    user {
                      id
                      name
                      email
                      phone_number
                      position
                      entity
                    }
                  }
                  account_managers {
                    id
                    user_id
                    name
                    title
                    entity
                    display_entity
                    mobile
                    email
                    user {
                      id
                      name
                      email
                      phone_number
                      position
                      entity
                    }
                  }
                  attachments {
                    id
                    file_name
                    original_filename
                    file_extension
                    path
                    uploaded_from
                  }
                  mou_effectiveness_cards {
                    id
                    first_party_labels
                    second_party
                    created_at
                    updated_at
                  }
                }
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
              }
            }
          `,
          variables: {
            first: this.first,
            page: this.page,
            is_pipeline: false,
            entity: this.filterByEntity,
            industry: this.filterByIndustry ? parseInt(this.filterByIndustry, 10) : null,
            type_of_collaboration: this.filterByTypeOfCollaboration && this.filterByTypeOfCollaboration.length > 0
              ? this.filterByTypeOfCollaboration
              : null,
            classifications: this.filterByClassifications && this.filterByClassifications.length > 0
              ? this.filterByClassifications
              : null,
            tier: this.filterByTier && this.filterByTier.length > 0
              ? this.filterByTier
              : null,
            search: this.searchText,
          },
          fetchPolicy: "network-only",
        });

        this.current_partners = data.current_partners;
      } catch (error) {
        console.error("Failed to fetch current partners:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPartnersStatistics() {
      try {
        const response = await this.$apollo.query({
          query: gql`
            query GetPartnersStatistics($is_pipeline: Boolean, $entity: String, $industry: Int, $type_of_collaboration: [String], $classifications: [String], $tier: [String], $search: String) {
              partnersStatistics(is_pipeline: $is_pipeline, entity: $entity, industry: $industry, type_of_collaboration: $type_of_collaboration, classifications: $classifications, tier: $tier, search: $search) {
                error
                error_code
                message
                data {
                  totalPartners
                  partnersByEntity {
                    entity
                    count
                  }
                  partnersByIndustry {
                    industry
                    count
                  }
                  partnersByTypeOfCollaboration {
                    type_of_collaboration
                    count
                  }
                  partnersByClassification {
                    classification
                    count
                  }
                  partnersByTier {
                    tier
                    count
                  }
                }
              }
            }
          `,
          variables: {
            is_pipeline: false,
            entity: this.filterByEntity || null,
            industry: this.filterByIndustry ? parseInt(this.filterByIndustry, 10) : null,
            type_of_collaboration: this.filterByTypeOfCollaboration && this.filterByTypeOfCollaboration.length > 0
              ? this.filterByTypeOfCollaboration
              : null,
            classifications: this.filterByClassifications && this.filterByClassifications.length > 0
              ? this.filterByClassifications
              : null,
            tier: this.filterByTier && this.filterByTier.length > 0
              ? this.filterByTier
              : null,
            search: this.searchText || null,
          },
          fetchPolicy: "network-only",
        });

        if (response.errors && response.errors.length > 0) {
          console.error("GraphQL Error:", response.errors);
          this.resetPartnersStatistics();
          return;
        }

        if (response.data.partnersStatistics.error) {
          console.error("GraphQL Error:", response.data.partnersStatistics.message);
          this.resetPartnersStatistics();
          return;
        }

        this.partnersStatistics = response.data.partnersStatistics.data;
      } catch (error) {
        console.error("Failed to load partners statistics:", error);
        this.resetPartnersStatistics();
      }
    },
    resetPartnersStatistics() {
      this.partnersStatistics = {
        totalPartners: 0,
        partnersByEntity: [],
        partnersByIndustry: [],
        partnersByTypeOfCollaboration: [],
        partnersByClassification: [],
        partnersByTier: [],
      };
    },
    onSearch(searchText) {
      this.searchText = searchText;
    },
    getEntityName(entity) {
      return entity.replace(/ATHS/g, "ATS");
    },
    getIndustryName(item) {
      if (item && item.industry_relation && item.industry_relation.name) {
        return item.industry_relation.name;
      }
      return '-';
    },
    formatClassifications(item) {
      if (!item || !item.classifications || item.classifications.length === 0) {
        return "-";
      }
      return item.classifications.join(", ");
    },
    getPartnerTier(item) {
      return evaluatePartnerTier(item);
    },
    partnerTierBadgeClass(tier) {
      return getTierBadgeClass(tier);
    },
    hasChildPartners(item) {
      return item && item.subsidiary_partners && item.subsidiary_partners.length > 0;
    },
    getChildPartnersCount(item) {
      return item && item.subsidiary_partners ? item.subsidiary_partners.length : 0;
    },
    setPage(page) {
      this.page = page;
      this.fetchCurrentPartners();
    },
    closeModal() {
      this.createModal = false;
      this.editModal = false;
      this.isViewItem = false;
      this.selectedItem = null;
    },
    getSrc(url) {
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    onCreated(msg) {
      this.showToast(msg);
    },
    onUpdated(msg) {
      this.showToast(msg);
    },
    showToast(message = "", shouldRefresh = true) {
      if (shouldRefresh) {
        this.fetchCurrentPartners();
        this.fetchPartnersStatistics();
      }
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: message,
      });
      if (shouldRefresh) {
        this.closeModal();
      }
    },
    async deleteItem(token) {
      let error_msg = null;
      let success_msg = null;
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            const DELETE_MUTATION = gql`
              mutation DeleteCurrentPartner($token: String!) {
                deleteCurrentPartner(token: $token) {
                  error
                  message
                }
              }
            `;

            try {
              await this.$apollo
                .mutate({
                  mutation: DELETE_MUTATION,
                  variables: {
                    token: token,
                  },
                })
                .then((data) => {
                  if (data.data.deleteCurrentPartner.error) {
                    error_msg = data.data.deleteCurrentPartner.message;
                  } else {
                    success_msg = data.data.deleteCurrentPartner.message;
                  }
                })
                .catch((error) => {
                  error_msg = error.message;
                });
            } catch (error) {
              error_msg = error.message || "Please try again.";
            }

            if (error_msg === null) {
              this.showToast(success_msg);
            } else {
              this.$swal
                .fire({
                  icon: "error",
                  title: "Oops...",
                  text: error_msg,
                })
                .then((result) => {
                  if (result.value || result.dismiss) {
                    error_msg = null;
                  }
                });
            }
          }
        });
    },
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    isShowOnLandingPage(item) {
      if (!item) {
        return false;
      }
      if (
        item.show_on_landing_page === false ||
        item.show_on_landing_page === 0 ||
        item.show_on_landing_page === "0"
      ) {
        return false;
      }
      return true;
    },
    onLandingVisibilityToggle(item, event) {
      if (!item || !item.token || !event || !event.target) {
        return;
      }
      if (this.landingVisibilityUpdatingToken === item.token) {
        event.target.checked = this.isShowOnLandingPage(item);
        return;
      }

      const showOnLandingPage = !!event.target.checked;
      const previousChecked = this.isShowOnLandingPage(item);
      if (showOnLandingPage === previousChecked) {
        return;
      }

      this.updateLandingPageVisibility(item, showOnLandingPage, event.target);
    },
    updatePartnerLandingVisibilityInList(token, showOnLandingPage) {
      if (!this.current_partners || !this.current_partners.data || !token) {
        return;
      }

      this.current_partners = {
        ...this.current_partners,
        data: this.current_partners.data.map((partner) => {
          if (partner.token !== token) {
            return partner;
          }
          return {
            ...partner,
            show_on_landing_page: !!showOnLandingPage,
          };
        }),
      };
    },
    async updateLandingPageVisibility(item, showOnLandingPage, inputEl) {
      if (!item || !item.token) {
        return;
      }

      const previousChecked = this.isShowOnLandingPage(item);
      this.landingVisibilityUpdatingToken = item.token;
      this.updatePartnerLandingVisibilityInList(item.token, showOnLandingPage);

      let error_msg = null;

      try {
        const result = await this.$apollo.mutate({
          mutation: UPDATE_LANDING_VISIBILITY_MUTATION,
          variables: {
            token: item.token,
            show_on_landing_page: showOnLandingPage,
          },
        });

        const payload = result && result.data && result.data.updateCurrentPartnerLandingVisibility
          ? result.data.updateCurrentPartnerLandingVisibility
          : null;

        if (!payload || payload.error) {
          error_msg = (payload && payload.message)
            ? payload.message
            : "Failed to update landing page visibility.";
        }
      } catch (error) {
        error_msg = error.message || "Failed to update landing page visibility.";
      }

      this.landingVisibilityUpdatingToken = null;

      if (error_msg !== null) {
        this.updatePartnerLandingVisibilityInList(item.token, previousChecked);
        if (inputEl) {
          inputEl.checked = previousChecked;
        }
        if (this.$swal) {
          this.$swal.fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          });
        }
        return;
      }

      this.showToast("Landing page visibility updated.", false);
    },
    viewMouEffectivenessCard(currentPartnerId, name) {
      this.selectedId = currentPartnerId;
      this.selectedTitle = name;
      this.isViewMouEffectivenessCard = true;
    },
    closeMouEffectivenessCard() {
      this.isViewMouEffectivenessCard = false;
      this.selectedId = null;
      this.selectedTitle = "";
      this.fetchCurrentPartners();
    },
    viewItem(item) {
      this.selectedItem = item;
      this.isViewItem = true;
    },
    openPartnerPage(item) {
      if (!item || !item.token) {
        return;
      }
      this.$router.push({
        name: "Current Partner View",
        params: { token: item.token },
      });
    },
    editItem(item) {
      this.selectedItem = item;
      this.editModal = true;
    },
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    getFormattedDate(date) {
      return formattedDate(date);
    },
    generateReport() {
      var store = this.$store;
      var apolloClient = this.$apollo && this.$apollo.provider
        ? this.$apollo.provider.defaultClient
        : null;
      if (!apolloClient) {
        return;
      }

      var fileName = "Current_Partners.xlsx";

      var variables = {
        is_pipeline: false,
        entity: this.filterByEntity || null,
        industry: this.filterByIndustry ? parseInt(this.filterByIndustry, 10) : null,
        type_of_collaboration: this.filterByTypeOfCollaboration && this.filterByTypeOfCollaboration.length > 0
          ? this.filterByTypeOfCollaboration
          : null,
        classifications: this.filterByClassifications && this.filterByClassifications.length > 0
          ? this.filterByClassifications
          : null,
        tier: this.filterByTier && this.filterByTier.length > 0
          ? this.filterByTier
          : null,
        search: this.searchText || null,
      };
      var variablesCopy = JSON.parse(JSON.stringify(variables));

      var self = this;

      var columns = [
        { key: "name", header: "NAME" },
        { key: "entity", header: "ENTITY" },
        { key: "industry", header: "INDUSTRY" },
        { key: "type_of_collaboration", header: "TYPE OF COLLABORATION" },
        { key: "classifications", header: "CLASSIFICATIONS" },
        { key: "tier", header: "TIER" },
        { key: "updated_at", header: "LAST UPDATED" },
        { key: "contact_person_name", header: "CONTACT PERSON NAME" },
        { key: "contact_person_designation", header: "DESIGNATION" },
        { key: "contact_person_phone", header: "PHONE NUMBER" },
        { key: "contact_person_email", header: "EMAIL ADDRESS" },
      ];

      var mapRow = function (obj) {
        var classifications = "";
        if (obj && obj.classifications && obj.classifications.length > 0) {
          classifications = obj.classifications.join(", ");
        }
        var contactPerson = (obj.contact_person && obj.contact_person.length > 0) ? obj.contact_person[0] : {};
        return {
          name: obj.name || "",
          entity: obj.entity || "",
          industry: (obj.industry_relation && obj.industry_relation.name) ? obj.industry_relation.name : "",
          type_of_collaboration: obj.type_of_collaboration || "",
          classifications: classifications,
          tier: self.getPartnerTier(obj) || "",
          updated_at: self.getFormattedDate(obj.updated_at),
          contact_person_name: contactPerson.name || "",
          contact_person_designation: contactPerson.designation || "",
          contact_person_phone: contactPerson.phone || "",
          contact_person_email: contactPerson.email || "",
        };
      };

      store
        .dispatch("exportManager/addJob", {
          fileName: fileName,
          totalSheets: 1,
        })
        .then(function (jobId) {
          store.dispatch("exportManager/setMinimized", false);
          store.dispatch("exportManager/updateJob", {
            id: jobId,
            progress: 5,
            currentSheet: "Current Partners",
          });

          runDetachedPartnerExport({
            store: store,
            apolloClient: apolloClient,
            jobId: jobId,
            fileName: fileName,
            variables: variablesCopy,
            columns: columns,
            mapRow: mapRow,
          });
        });
    },
    formatNumber(num) {
      return num.toLocaleString();
    },
    getTopEntity() {
      if (this.partnersStatistics.partnersByEntity && this.partnersStatistics.partnersByEntity.length > 0) {
        return this.partnersStatistics.partnersByEntity[0].entity.replace(/ATHS/g, "ATS");
      }
      return "N/A";
    },
    getTopEntityCount() {
      if (this.partnersStatistics.partnersByEntity && this.partnersStatistics.partnersByEntity.length > 0) {
        return this.partnersStatistics.partnersByEntity[0].count;
      }
      return 0;
    },
    getTopIndustry() {
      if (this.partnersStatistics.partnersByIndustry && this.partnersStatistics.partnersByIndustry.length > 0) {
        return this.partnersStatistics.partnersByIndustry[0].industry;
      }
      return "N/A";
    },
    getTopIndustryCount() {
      if (this.partnersStatistics.partnersByIndustry && this.partnersStatistics.partnersByIndustry.length > 0) {
        return this.partnersStatistics.partnersByIndustry[0].count;
      }
      return 0;
    },
    onEntityChartClick(entity) {
      if (!entity) {
        return;
      }
      this.openChartDrilldown("entity", entity, this.getEntityName(entity));
    },
    onIndustryChartClick(industryName) {
      if (!industryName) {
        return;
      }
      var industries = this.industries || [];
      var matched = null;
      for (var i = 0; i < industries.length; i++) {
        if (industries[i].name === industryName) {
          matched = industries[i];
          break;
        }
      }
      if (!matched) {
        return;
      }
      this.openChartDrilldown("industry", parseInt(matched.id, 10), industryName);
    },
    onTypeOfCollaborationChartClick(type) {
      if (!type) {
        return;
      }
      this.openChartDrilldown("type_of_collaboration", type, type);
    },
    onClassificationChartClick(label) {
      if (!label) {
        return;
      }
      this.openChartDrilldown("classification", label, label);
    },
    getTierCount(tierName) {
      var tiers = this.partnersStatistics && this.partnersStatistics.partnersByTier ? this.partnersStatistics.partnersByTier : [];
      for (var i = 0; i < tiers.length; i++) {
        if (tiers[i].tier === tierName) {
          return this.formatNumber(tiers[i].count);
        }
      }
      return '0';
    },
    onTierChartClick(tier) {
      if (!tier) {
        return;
      }
      this.openChartDrilldown("tier", tier, tier);
    },
    openChartDrilldown(dimension, value, label) {
      this.chartDrilldownDimension = dimension;
      this.chartDrilldownValue = value;
      this.chartDrilldownLabel = label || "";
      this.isChartDrilldownOpen = true;
    },
    closeChartDrilldownModal() {
      this.isChartDrilldownOpen = false;
      this.chartDrilldownDimension = "";
      this.chartDrilldownValue = null;
      this.chartDrilldownLabel = "";
    },
    onStatusChartClick(status) {
      // You can add status filtering logic here if needed
      console.log('Status clicked:', status);
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }
.slide-enter-active,
.slide-leave-active { transition: transform 0.3s ease; }
.slide-enter-from,
.slide-leave-to { transform: translateX(-100%); }

/* ── Sidebar ── */
.cp-sidebar {
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);
}
.cp-select {
  padding: 0.5rem 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  outline: none;
  background: #fff;
  transition: border-color 0.2s;
  &:focus { border-color: #3b82f6; }
}

/* ── Buttons ── */
.cp-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 1.125rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 12px -2px rgba(37, 99, 235, 0.45); }
}
.cp-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #f9fafb; border-color: #d1d5db; }
}
.cp-btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
}

/* ── Stat / Tier Cards ── */
.cp-stat-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.25rem 1rem;
  border-radius: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s, transform 0.2s;
}
.cp-stat-card--clickable {
  cursor: pointer;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px -6px rgba(0, 0, 0, 0.1);
  }
}
.cp-stat-card__icon {
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  font-size: 1.125rem;
}
.cp-stat-card__body { min-width: 0; }
.cp-stat-card__value {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
  color: #1e293b;
}
.cp-stat-card__label {
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0.125rem 0 0;
  color: #475569;
}
.cp-stat-card__hint {
  font-size: 0.6875rem;
  margin: 0.125rem 0 0;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cp-stat-card--total .cp-stat-card__icon { background: #dbeafe; color: #2563eb; }
.cp-stat-card--strategic { border-left: 3px solid #7c3aed; }
.cp-stat-card--strategic .cp-stat-card__icon { background: #ede9fe; color: #7c3aed; }
.cp-stat-card--active { border-left: 3px solid #2563eb; }
.cp-stat-card--active .cp-stat-card__icon { background: #dbeafe; color: #2563eb; }
.cp-stat-card--engaged { border-left: 3px solid #d97706; }
.cp-stat-card--engaged .cp-stat-card__icon { background: #fef3c7; color: #d97706; }
.cp-stat-card--shelved { border-left: 3px solid #64748b; }
.cp-stat-card--shelved .cp-stat-card__icon { background: #f1f5f9; color: #64748b; }

/* ── Table ── */
.cp-thead-row { background: #f8fafc; border-bottom: 2px solid #e2e8f0; }
.cp-th {
  padding: 0.75rem 1.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}
.cp-row {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
  &:hover { background: #f8fafc; }
}
.cp-row--subsidiary {
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
}
.cp-td {
  padding: 0.625rem 1.25rem;
  font-size: 0.8125rem;
  color: #374151;
  vertical-align: middle;
}
.cp-name-link {
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  font: inherit;
  font-weight: 600;
  color: #1e3a8a;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
}
.cp-name-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

/* ── Action Buttons ── */
.cp-action-btn {
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  border: none;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  font-size: 0.8125rem;
  transition: all 0.15s;
  &:hover { background: #e0e7ff; color: #4338ca; }
}
.cp-action-btn--danger:hover { background: #fee2e2; color: #dc2626; }

/* ── Toggle switch ── */
.landing-visibility-cell { display: flex; align-items: center; justify-content: center; min-width: 5rem; }
.landing-visibility-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.landing-visibility-switch-disabled { opacity: 0.5; pointer-events: none; }
.landing-visibility-switch-input {
  position: absolute; inset: 0; width: 100%; height: 100%;
  margin: 0; opacity: 0; cursor: pointer; z-index: 2;
}
.landing-visibility-switch-input:disabled { cursor: not-allowed; }
.landing-visibility-switch-slider {
  position: absolute; cursor: pointer; inset: 0;
  background-color: #cbd5e1; border-radius: 34px; transition: background-color 0.25s;
}
.landing-visibility-switch-slider::before {
  position: absolute; content: ""; height: 18px; width: 18px;
  left: 3px; bottom: 3px; background: #fff; border-radius: 50%; transition: transform 0.25s;
}
.landing-visibility-switch-input:checked + .landing-visibility-switch-slider { background-color: #2563eb; }
.landing-visibility-switch-input:checked + .landing-visibility-switch-slider::before { transform: translateX(20px); }
.landing-visibility-switch-input:focus + .landing-visibility-switch-slider { box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25); }

.landing-visibility-badge { display: inline-flex; align-items: center; padding: 0.2rem 0.5rem; border-radius: 9999px; font-size: 0.6875rem; font-weight: 600; }
.landing-visibility-badge-yes { background: #dcfce7; color: #166534; }
.landing-visibility-badge-no { background: #f1f5f9; color: #64748b; }

/* ── Tier badges ── */
.partner-tier-badge {
  display: inline-flex; align-items: center; padding: 0.2rem 0.5rem;
  border-radius: 9999px; font-size: 0.6875rem; font-weight: 700; white-space: nowrap; letter-spacing: 0.02em;
}
.partner-tier-badge-strategic { background: #ede9fe; color: #6d28d9; }
.partner-tier-badge-active { background: #dbeafe; color: #1d4ed8; }
.partner-tier-badge-engaged { background: #fef3c7; color: #b45309; }
.partner-tier-badge-shelved { background: #f1f5f9; color: #475569; }
</style>
